import { Alert, Grid2, TextField, Typography } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	EDGER_ROLL_INITIAL_VALUES,
	EdgerRoll,
} from "@/pages/catalogs/models/EdgerRoll";
import { upsertEdgerRoll } from "@/pages/catalogs/repositories/EdgerRollRepository";
import { RollsetCard } from "@/pages/catalogs/shared/components/RollsetCard";
import {
	RollsetDeactivationConfirmModal,
	RollsetInfo,
} from "@/pages/catalogs/shared/components/RollsetDeactivationConfirmModal";
import { CommentsField } from "@/shared/components/CommentsField";
import { RollStatusSelector } from "@/shared/selectors/RollStatusSelector";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	LocationSelector,
	LocationTreeSelector,
	RollPositionSelector,
} from "../../../../../shared/selectors";
import { PassesGrid } from "./passes/PassesGrid";

type Props = {
	roll: EdgerRoll | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const EdgerRollDetails = ({ roll, modal, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingFormData, setPendingFormData] = useState<EdgerRoll | null>(
		null,
	);

	const succesfullyMessage = `The roll was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<EdgerRoll>({
		defaultValues: EDGER_ROLL_INITIAL_VALUES,
		mode: "onChange",
	});

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) || modal == "create";

	const validateDiameters = (
		value: number | null,
		field: "Diameter" | "NewDiameter" | "ScrapDiameter",
	) => {
		const currentDiameter = getValues("Diameter") || 0;

		if (value === null || value === undefined) return true;

		switch (field) {
			case "NewDiameter":
				return (
					value >= currentDiameter ||
					"Must be greater than or equal to the current diameter"
				);
			case "ScrapDiameter":
				return (
					value < currentDiameter || "Must be less than the current diameter"
				);
			default:
				return true;
		}
	};
	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertEdgerRoll, {
		onSuccess: async (id: number) => {
			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries({ queryKey: ["edgerRolls"] });
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const willDeactivateRollset = (formData: EdgerRoll): boolean => {
		if (!roll || modal === "create") return false;

		const statusChangedToBroken =
			formData.RollStatusCode === "K" && roll.RollStatusCode !== "K";
		if (!statusChangedToBroken) return false;

		return roll.IsInActiveRollset && !roll.IsInActiveCarrier;
	};

	const executeSubmit = (data: EdgerRoll) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (roll?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	const onSubmit: SubmitHandler<EdgerRoll> = async (data) => {
		if (modal === "delete") return;

		if (willDeactivateRollset(data)) {
			setPendingFormData(data);
			setShowConfirmDialog(true);
			return;
		}

		executeSubmit(data);
	};

	const handleConfirmDeactivation = () => {
		setShowConfirmDialog(false);
		if (pendingFormData) {
			executeSubmit(pendingFormData);
			setPendingFormData(null);
		}
	};

	const handleCancelDeactivation = () => {
		setShowConfirmDialog(false);
		setPendingFormData(null);
	};

	useEffect(() => {
		if (modal !== "") {
			reset(roll || EDGER_ROLL_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, roll]);

	// Rollset info for confirmation dialog
	const rollsetInfo: RollsetInfo | null = roll?.RollsetId
		? {
				RollsetId: roll.RollsetId,
				RollsetLetter: roll.RollsetLetter,
				RollsetSectionName: roll.RollsetSectionName,
				RollsetStandName: roll.RollsetStandName,
				RollsetTopRollName: roll.RollsetTopRollName,
				RollsetBottomRollName: roll.RollsetBottomRollName,
			}
		: null;

	const disabledStatusSelector = roll?.IsInActiveCarrier;

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						roll === null ? "New Roll" : `${roll?.PartName ?? ""}`
					} - Edger Rolls`}
				/>
			)}
			{roll === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a roll or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						{!!roll?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="warning">
									This roll is in Rollset {roll?.RollsetLetter} (Section:{" "}
									{roll?.RollsetSectionName}, Stand: {roll?.RollsetStandName})
									which is assigned to carrier{" "}
									<strong>{roll?.CarrierName}</strong> (Status:{" "}
									{roll?.CarrierStatusName}).
								</Alert>
							</Grid2>
						) : null}
						{!!roll?.IsInActiveRollset && !roll?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="info">
									This roll is in an active Rollset {roll?.RollsetLetter}{" "}
									(Section: {roll?.RollsetSectionName}, Stand:{" "}
									{roll?.RollsetStandName}).
								</Alert>
							</Grid2>
						) : null}
						<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "edger-roll-name-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RollStatusCode"
								control={control}
								rules={{ required: "Status is required" }}
								render={({ field, fieldState: { error } }) => (
									<RollStatusSelector
										label="Status"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete" || disabledStatusSelector}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="NewDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "NewDiameter"),
									},
									deps: ["Diameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Initial Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "edger-roll-new-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="Diameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "Diameter"),
									},
									deps: ["NewDiameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Current Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "edger-roll-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="ScrapDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "ScrapDiameter"),
									},
									deps: ["Diameter", "NewDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Scrap Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "edger-roll-scrap-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
							<Controller
								name="RFIDTag"
								control={control}
								render={({ field }) => (
									<TextField
										label="Tag"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RollPosition"
								control={control}
								rules={{ required: "Position is required" }}
								render={({ field, fieldState: { error } }) => (
									<RollPositionSelector
										required
										label="Position"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="StandId"
								control={control}
								rules={{ required: "Stand is required" }}
								render={({ field, fieldState: { error } }) => (
									<LocationSelector
										label="Stand"
										value={field.value}
										onChange={field.onChange}
										onlyStands
										fieldError={error}
									/>
								)}
							/>
						</Grid2>
						{roll?.RollsetId ? (
							<Grid2 size={{ md: 6, xs: 12 }}>
								<RollsetCard
									sectionName={roll.RollsetSectionName!}
									standName={roll.RollsetStandName!}
									rollsetLetter={roll.RollsetLetter}
									topRollName={roll.RollsetTopRollName!}
									bottomRollName={roll.RollsetBottomRollName!}
									isActualTopRoll={roll.RollsetTopRollId === roll.PartId}
									status={roll.RollStatusCode!}
								/>
							</Grid2>
						) : (
							<Grid2 size={{ md: 2, xs: 0 }} />
						)}
						<Grid2 size={{ md: roll?.RollsetId ? 6 : 8, xs: 12 }}>
							<PassesGrid
								partId={roll?.PartId!}
								disabled={modal === "delete"}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<RollsetDeactivationConfirmModal
				show={showConfirmDialog}
				entityName={roll?.PartName || ""}
				entityType="Roll"
				actionDescription={`Changing the status to 'Broken' of this roll`}
				rollsetInfo={rollsetInfo}
				onConfirm={handleConfirmDeactivation}
				onCancel={handleCancelDeactivation}
			/>
		</>
	);
};
